import React, { useState, useEffect, useContext, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';

import { useTheme, Box, CircularProgress, Grid } from '@mui/material';

import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import SideBar from '../components/SideBar';
import Header from '../components/Header';
import config from '../config';

import { AuthenticationService } from '../services';
import { UserContext } from '../contexts';
import { useRef } from 'react';

const AdminPanel = ({ Component }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const language = i18n.language;
  const scrollRef = useRef(null);

  const { setCurrentUser, setIsSuperAdmin } = useContext(UserContext);

  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  useEffect(() => {
    const isLoggedIn = AuthenticationService.isLoggedIn();
    if (isLoggedIn) {
      const currentUser = AuthenticationService.getCurrentUser();
      setCurrentUser(currentUser);
      setIsSuperAdmin(currentUser?.id === +config.superAdminId);
    } else navigate(`/auth?returnUrl=${encodeURIComponent(window.location.href.replace(window.location.origin, ''))}`);
  }, [navigate, setCurrentUser, setIsSuperAdmin]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [location]);

  return (
    <Grid
      ref={scrollRef}
      container
      sx={{
        flexWrap: 'nowrap',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'auto',
      }}>
      <Grid
        item
        xs={12}
        container
        sx={{
          zIndex: 100,
          position: 'sticky',
          top: 0,
          px: { xs: theme.spacing(2), sm: theme.spacing(6), md: theme.spacing(8) },
          boxShadow: 'none',
          backgroundColor: 'secondary.main',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: '7rem',
          maxHeight: '7rem',
        }}>
        <TopBar handleDrawerToggle={handleDrawerToggle} />
      </Grid>
      <Grid
        item
        xs={12}
        container
        sx={{
          gap: theme.spacing(3),
          pr: language === 'en' ? theme.spacing(7) : 0,
          pl: language === 'ar' ? theme.spacing(7) : 0,
          flexWrap: 'nowrap',
          backgroundColor: theme.palette.brown[50],
        }}>
        <Grid item sx={{ maxHeight: 'calc(100vh - 6rem)', position: 'sticky', top: '7rem' }}>
          <SideBar mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />
        </Grid>
        <Grid
          item
          sx={{
            pt: theme.spacing(7),
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflowX: 'auto',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2),
              height: '100%',
            }}>
            <Header />
            <Box
              elevation={0}
              style={{
                padding: theme.spacing(2),
                backgroundColor: 'white',
                height: '100%',
              }}>
              <Suspense
                fallback={
                  <Box display='flex' flexDirection='column' alignItems='center' justifyContent='center' flexGrow={1} height='100%'>
                    <CircularProgress />
                  </Box>
                }>
                <Component />
              </Suspense>
            </Box>
          </Box>
          <Footer />
        </Grid>
      </Grid>
    </Grid>
  );
};

AdminPanel.propTypes = {
  window: PropTypes.func,
};

export default AdminPanel;
